import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import type { AvailableCouponsResult } from '@lov/shared';
import { TicketCouponCard } from './TicketCouponCard';

interface SmartCouponSectionProps {
  couponsData: AvailableCouponsResult;
  onApplyCoupon: (code: string) => void;
  appliedCouponCode?: string;
  applyingCouponCode?: string;
}

export function SmartCouponSection({
  couponsData,
  onApplyCoupon,
  appliedCouponCode,
  applyingCouponCode,
}: SmartCouponSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const { availableCoupons, nearlyUnlockedCoupons, lockedCoupons, bestCoupon, maxPotentialSavings, totalCouponCount } =
    couponsData;

  // Empty state
  if (totalCouponCount === 0) {
    return (
      <Box
        sx={{
          p: 3,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'grey.300',
        }}
      >
        <LocalOfferIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
        <Typography variant="body1" color="text.secondary">
          No coupons available at the moment
        </Typography>
      </Box>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Box>
      {/* Header with max savings badge */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          Available Offers
        </Typography>
        {maxPotentialSavings > 0 && (
          <Chip
            icon={<LocalOfferIcon />}
            label={`Save up to ${formatCurrency(maxPotentialSavings)}`}
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>

      {/* Best coupon section */}
      {bestCoupon && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="primary" fontWeight={600} sx={{ mb: 1 }}>
            Best for You
          </Typography>
          <TicketCouponCard
            coupon={bestCoupon}
            onApply={onApplyCoupon}
            isApplying={applyingCouponCode === bestCoupon.code}
            isApplied={appliedCouponCode === bestCoupon.code}
          />
        </Box>
      )}

      {/* View all button */}
      {totalCouponCount > 1 && (
        <Button
          fullWidth
          variant="outlined"
          endIcon={isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{ mb: 2 }}
        >
          {isExpanded ? 'Show Less' : `View All ${totalCouponCount} Coupons`}
        </Button>
      )}

      {/* Expanded section */}
      <Collapse in={isExpanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Available coupons (excluding best coupon) */}
          {availableCoupons.length > 1 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ mb: 1 }}>
                Available Now
              </Typography>
              {availableCoupons
                .filter((coupon) => coupon.code !== bestCoupon?.code)
                .map((coupon) => (
                  <Box key={coupon.code} sx={{ mb: 1.5 }}>
                    <TicketCouponCard
                      coupon={coupon}
                      onApply={onApplyCoupon}
                      isApplying={applyingCouponCode === coupon.code}
                      isApplied={appliedCouponCode === coupon.code}
                      variant="compact"
                    />
                  </Box>
                ))}
            </Box>
          )}

          {/* Nearly unlocked coupons */}
          {nearlyUnlockedCoupons.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="warning.main" fontWeight={600} sx={{ mb: 1 }}>
                Almost Unlocked
              </Typography>
              {nearlyUnlockedCoupons.map((coupon) => (
                <Box key={coupon.code} sx={{ mb: 1.5 }}>
                  <TicketCouponCard
                    coupon={coupon}
                    onApply={onApplyCoupon}
                    isApplying={applyingCouponCode === coupon.code}
                    isApplied={appliedCouponCode === coupon.code}
                    variant="compact"
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Locked coupons */}
          {lockedCoupons.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.disabled" fontWeight={600} sx={{ mb: 1 }}>
                Locked Offers
              </Typography>
              {lockedCoupons.map((coupon) => (
                <Box key={coupon.code} sx={{ mb: 1.5 }}>
                  <TicketCouponCard
                    coupon={coupon}
                    onApply={onApplyCoupon}
                    isApplying={applyingCouponCode === coupon.code}
                    isApplied={appliedCouponCode === coupon.code}
                    variant="compact"
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
